/**
 * Statistics & Data Handling: mean/median/mode, probability basics.
 * (Reading charts/tables/graphs is covered by pie-chart game.)
 */

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const makeNumericDistractors = (correct, count = 3) => {
    const base = Number(correct);
    const seen = new Set([String(correct)]);
    const out = [];
    const step = base < 10 ? 1 : base < 50 ? 2 : 5;
    const offsets = [-step * 2, -step, step, step * 2];
    while (out.length < count) {
        const off = offsets[Math.floor(Math.random() * offsets.length)];
        const val = base + off;
        if (val >= 0 && !seen.has(String(val))) {
            seen.add(String(val));
            out.push(String(val));
        }
    }
    return out.slice(0, count);
};

/** Calculating mean, median and mode */
export const generateMeanMedianModeQuestion = (difficulty = "normal") => {
    const askMean = () => {
        const len = difficulty === "easy" ? 4 : difficulty === "hard" ? 6 : 5;
        const values = [];
        for (let i = 0; i < len; i++) {
            values.push(Math.floor(Math.random() * 20) + 1);
        }
        const sum = values.reduce((a, b) => a + b, 0);
        const mean = sum / len;
        const exact = mean === Math.round(mean);
        const answer = exact ? String(Math.round(mean)) : String(mean);
        const list = values.join(", ");
        return { question: `What is the mean of these numbers?`, values, answer, options: shuffle([answer, ...makeNumericDistractors(Number(answer), 3)]), measure: "mean" };
    };

    const askMedian = () => {
        const len = 5;
        const values = [];
        for (let i = 0; i < len; i++) {
            values.push(Math.floor(Math.random() * 20) + 1);
        }
        values.sort((a, b) => a - b);
        const median = values[Math.floor(len / 2)];
        const answer = String(median);
        const list = values.join(", ");
        return { question: `What is the median of these numbers?`, values: [...values], answer, options: shuffle([answer, ...makeNumericDistractors(median, 3)]), measure: "median" };
    };

    const askMode = () => {
        const values = [2, 3, 3, 4, 5, 3, 2, 3];
        const counts = {};
        values.forEach((v) => { counts[v] = (counts[v] || 0) + 1; });
        const maxCount = Math.max(...Object.values(counts));
        const mode = Number(Object.keys(counts).find((k) => counts[k] === maxCount));
        const answer = String(mode);
        return { question: `What is the mode of these numbers?`, values, answer, options: shuffle([answer, ...makeNumericDistractors(mode, 3)]), measure: "mode" };
    };

    const generators = difficulty === "easy" ? [askMean, askMedian] : [askMean, askMedian, askMode];
    const result = generators[Math.floor(Math.random() * generators.length)]();
    return {
        ...result,
        type: "mean-median-mode",
        mode: "mean-median-mode",
        visualType: "data-list",
    };
};

/** Probability basics — chance of simple events */
export const generateProbabilityQuestion = (difficulty = "normal") => {
    const questions = [
        {
            q: "What is the probability of getting a 6 when you roll a fair dice?",
            answer: "1/6",
            options: ["1/6", "1/2", "1/3", "1/4"],
        },
        {
            q: "A bag has 3 red and 2 blue balls. What is the probability of picking a red ball?",
            answer: "3/5",
            options: ["3/5", "2/5", "1/2", "3/4"],
        },
        {
            q: "A bag has 2 green and 4 yellow balls. What is the probability of picking a green ball?",
            answer: "2/6",
            options: ["2/6", "4/6", "1/2", "1/3"],
        },
        {
            q: "What is the probability of getting heads when you flip a fair coin?",
            answer: "1/2",
            options: ["1/2", "1/4", "1", "0"],
        },
        {
            q: "There are 5 blue and 5 red marbles. What is the probability of picking a blue marble?",
            answer: "1/2",
            options: ["1/2", "1/5", "5/10", "1/10"],
        },
        {
            q: "A spinner has 4 equal sections (red, blue, green, yellow). What is the probability of landing on red?",
            answer: "1/4",
            options: ["1/4", "1/2", "1/3", "4"],
        },
        {
            q: "A bag has 1 white and 3 black balls. What is the probability of picking the white ball?",
            answer: "1/4",
            options: ["1/4", "1/3", "3/4", "1/2"],
        },
    ];

    const item = questions[Math.floor(Math.random() * questions.length)];
    const options = shuffle([item.answer, ...item.options.filter((o) => o !== item.answer)].slice(0, 4));
    return {
        question: item.q,
        answer: item.answer,
        options,
        type: "probability-basics",
        mode: "probability",
        visualType: "probability",
    };
};

export const generateStatisticsDataQuestionByKey = (gameKey, difficulty = "normal") => {
    switch (gameKey) {
        case "mean-median-mode":
            return generateMeanMedianModeQuestion(difficulty);
        case "probability-basics":
            return generateProbabilityQuestion(difficulty);
        default:
            return generateMeanMedianModeQuestion(difficulty);
    }
};
